export default `# Miro Integration

## What Does It Do?

The Miro integration connects your Spectrum project to a Miro board so you can:

- **Push session outputs to Miro** — Personas, user stories, decisions, and pain points appear as color-coded sticky notes on your board
- **Detect edits made on Miro** — If someone changes a sticky on the board, you'll see the conflict and can choose which version to keep
- **Ingest freeform Miro content into the knowledge graph** — Select frames from your board, and an AI will summarize the discussion, extract entities, and link everything into your project's research graph

---

## Getting Started

### Step 1: Connect to Miro

Open the **Miro** panel in your session. If Miro isn't connected yet, you'll see a **Connect to Miro** button. Click it to authorize Spectrum with your Miro workspace.

This only needs to be done once per organization — all projects share the same connection.

### Step 2: Create a Project Board

Once connected, you'll see a list of template frames available for your board. These are pre-built layouts set up by your admin.

1. Select the frames you want included — all are selected by default
2. Click **Create Board**
3. Wait a few seconds for the board to be created

Your project now has its own Miro board with the selected template frames copied in. Click the board link at any time to open it in Miro.

### Step 3: Add More Templates Later

After board creation, you can still add template frames you didn't include initially. In the **Template Frames** section, any frame not yet on your board shows an **Add** button.

---

## Syncing Session Content to Miro

When you're in a session, the Miro panel shows a **Sync to Miro** button.

### What Gets Synced

| Entity Type | Sticky Color | Column |
|------------|-------------|--------|
| Personas | Yellow | 1st |
| Pain Points | Red | 2nd |
| User Stories | Blue | 3rd |
| Decisions | Green | 4th |
| Actions | Violet | 5th |

Each entity becomes a sticky note organized into columns within a session frame on your board.

### How to Sync

1. Click **Sync to Miro**
2. The panel updates to show counts per category
3. Open your Miro board to see the stickies

You can sync multiple times — new items get created, changed items get updated, unchanged items are skipped.

---

## Detecting Changes from Miro

If collaborators edit sticky notes directly on the Miro board, you can detect those changes.

1. Click **Check Changes** in the Miro panel
2. The system compares every synced sticky against what Spectrum has
3. If differences are found, they appear in a **Conflicts** section

Each conflict shows both versions side by side. You can:
- **Keep Ours** — Overwrite the Miro sticky with the Spectrum version
- **Keep Miro** — Accept the Miro edit as the new value

---

## Ingesting Miro Content into the Graph

This feature uses AI to analyze freeform content on your Miro board and pull insights into the knowledge graph — even content that doesn't map neatly to personas or user stories.

### When to Use This

- After a workshop where participants added notes and discussion points
- When frames contain brainstorming output or unstructured research
- To connect themes across different frames automatically

### How to Ingest

1. Scroll to the **Ingest from Miro** section
2. Toggle **Entire Board** or select specific frames
3. Click **Ingest to Graph**

For each frame, the AI summarizes the discussion, extracts key themes, identifies entities, and links them to your existing graph data.

If you ingest 2 or more frames, a second AI pass automatically finds thematic connections between frames.

### What Appears in the Graph

- **Miro Board** (teal, "M") — Your project's board
- **Miro Insight** (amber, "MI") — One per ingested frame with AI summary and themes
- Connecting edges linking insights to existing personas, stories, and decisions

---

## Tips

- **Sync before ingesting** — Sync session entities to Miro first so the AI can match against them during ingestion
- **Use descriptive frame titles** — The AI uses titles as context. "Sprint 3 Retro Notes" is better than "Frame 1"
- **Check the graph after ingesting** — The thematic links between frames often surface patterns not obvious from the board alone
- **Resolve conflicts promptly** — Unresolved conflicts can cause divergence between Spectrum and Miro`;
